import { IConfig } from "@kuika/kuika-cl-model";
import * as React from "react";
import { SaveStatus } from "../../../shared/utility/save-status";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import { ConfigScope, getConfigNameScope, normalizeConfigName } from "./config-manager-model";
import { ConfigNameModal } from "./config-name-modal";
import { requestConfigReveal } from "./config-reveal";

/**
 * The configuration name sheet, driven by what it is doing: create, create from a global
 * configuration, or rename.
 *
 * Lifted out of the landing so the editor's switcher can offer the same Rename and "Create
 * config from this global config" its list does — the landing unmounts while a configuration is
 * open, and a dialog that lived only there would take those actions with it.
 *
 * What the sheet is doing is one value rather than a boolean plus "which row, if any" — a
 * `create` with a pending global row sitting around from the last "create from global" used to
 * be representable and meaningless at the same time.
 */
export type ConfigNameDialogState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "createFromGlobal"; source: IConfig }
  | { kind: "rename"; row: IConfig };

export interface IConfigNameDialogProps {
  scope: ConfigScope;
  /** Every configuration the surface knows about; the name must be unique within them. */
  configs: IConfig[];
  dialog: ConfigNameDialogState;
  onClose: () => void;
  onCreateConfig: (name: string, fromGlobal?: IConfig) => void;
  /**
   * Resolves `true` once the rename is stored, `false` when the host declined to write it (the
   * stale-write prompt's Cancel — "go back and refresh first"), which closes the sheet without a
   * ✓. Rejecting leaves the dialog open so the user can retry; the host has already reported the
   * failure.
   */
  onRenameConfig?: (config: IConfig, name: string) => Promise<boolean>;
}

interface IConfigNameDialogState {
  /**
   * Where a rename's write stands. Create is not a write here — it opens the editor — so only a
   * rename puts a status on the confirm.
   */
  renameStatus: KuikaButtonStatus;
}

/** What the sheet shows for the dialog currently open. */
interface INameDialogCopy {
  title: string;
  subtitle?: string;
  confirmLabel: string;
  initialValue?: string;
  selfId?: string;
  originalName?: string;
  /** Only a rename writes, so only a rename has a status for the confirm. */
  status?: KuikaButtonStatus;
}

export class ConfigNameDialog extends React.PureComponent<IConfigNameDialogProps, IConfigNameDialogState> {
  state: IConfigNameDialogState = { renameStatus: "idle" };

  /** Holds the rename's ✓ before the sheet closes — see `SaveStatus`, which owns that order. */
  private readonly renameStatus: SaveStatus = new SaveStatus((renameStatus: KuikaButtonStatus): void => {
    this.setState({ renameStatus });
  });

  componentWillUnmount(): void {
    this.renameStatus.dispose();
  }

  close = (): void => {
    this.renameStatus.reset();
    this.props.onClose();
  };

  handleSubmit = (name: string): void => {
    const normalized: string = normalizeConfigName(name);
    const dialog: ConfigNameDialogState = this.props.dialog;

    if (dialog.kind === "rename") {
      // A rename that renames nothing is not a write: the update PUTs the whole document, so
      // "confirm without changing it" would otherwise rewrite every setting just to close.
      if (!this.props.onRenameConfig || normalized === normalizeConfigName(dialog.row.name ?? "")) {
        this.close();
        return;
      }
      // The confirm is clickable again the moment its ✓ replaces the spinner; a second click
      // would re-send the rename behind the closing sheet.
      if (!this.renameStatus.canStart) return;
      this.renameStatus.start();
      this.props
        .onRenameConfig(dialog.row, normalized)
        .then((isStored: boolean): void => {
          // Declined, not failed: nothing was written, so there is no ✓ to hold and no row to
          // light — the sheet steps aside and the host refreshes the list.
          if (!isStored) {
            this.close();
            return;
          }
          // The ✓ first, then the sheet closes. A rename can move the row under the list's name
          // sort, so the list lights it where it went once the refetch carries the new name. From
          // the editor's switcher there is no list on screen, and the request lapses unless the
          // reader goes back to it at once.
          // `onClose`, not `close`: the machine returns the confirm to idle itself, after the close,
          // so the sheet does not repaint "Rename" over its ✓ on the way out.
          this.renameStatus.succeed((): void => {
            this.props.onClose();
            requestConfigReveal({ scope: this.props.scope, name: normalized });
          });
        })
        // The host has already reported the failure; leaving the dialog open — rather than
        // closing it on a write that did not happen — is what lets the user retry, and the
        // confirm says so.
        .catch((): void => this.renameStatus.fail());
      return;
    }

    const fromGlobal: IConfig | undefined = dialog.kind === "createFromGlobal" ? dialog.source : undefined;
    this.close();
    this.props.onCreateConfig(normalized, fromGlobal);
  };

  /** The sheet's copy and its rename-only rule inputs, one switch on the dialog's kind. */
  getCopy = (): INameDialogCopy => {
    const dialog: ConfigNameDialogState = this.props.dialog;

    if (dialog.kind === "createFromGlobal") {
      return {
        title: "Create from a global configuration",
        subtitle: "Your configuration starts from the workspace one's settings — change any of them afterwards.",
        confirmLabel: "Create",
        initialValue: dialog.source.name
      };
    }

    if (dialog.kind === "rename") {
      return {
        title: "Rename configuration",
        confirmLabel: "Rename",
        initialValue: dialog.row.name,
        selfId: dialog.row.id,
        originalName: dialog.row.name,
        // Unlike create, a rename is a network write, so the confirm says where it stands.
        status: this.state.renameStatus
      };
    }

    return {
      title: "Create a configuration",
      subtitle: "Configurations are environments: give this one a name you will recognise when you publish.",
      confirmLabel: "Create"
    };
  };

  render() {
    const copy: INameDialogCopy = this.getCopy();
    return (
      <ConfigNameModal
        visible={this.props.dialog.kind !== "closed"}
        title={copy.title}
        subtitle={copy.subtitle}
        confirmLabel={copy.confirmLabel}
        isLoading={false}
        status={copy.status}
        // Read-only references are excluded: "create a config from this global config" would
        // otherwise open prefilled with a name the dialog itself rejects.
        configs={getConfigNameScope(this.props.scope, this.props.configs)}
        initialValue={copy.initialValue}
        selfId={copy.selfId}
        originalName={copy.originalName}
        onCancel={this.close}
        onSubmit={this.handleSubmit}
      />
    );
  }
}
